/**
 * Fan wind field (M16): fans are elements marbles physically INTERACT
 * with. Every endless fan placement contributes a capsule of moving air
 * — origin, unit blow direction, length, radius, strength — and the
 * per-marble force pass adds mass · strength · falloff · direction while
 * a racer is inside the capsule.
 *
 * Geometry is DERIVED from the placement (deterministic, no rng):
 *  - a STANDING fan blows along its local +z rotated by the placement
 *    yaw — the legacy ledge fans face the channel, so their capsules
 *    CROSS it: lateral gusts fall out of existing furniture for free;
 *  - a TABLE fan lies flat and blows straight up — an updraft column.
 *
 * The optional `placement.wind` hint (shared/types.ts) tunes strength /
 * length / radius and nudges the capsule origin in world units — that is
 * how the boost-corridor fan parks its wash over the channel while its
 * housing stays on the ledge, and how the lip updraft starts low enough
 * to catch a rolling marble.
 *
 * Falloff model: axial linear from 1 at the fan face to 0 at `length`;
 * radial full inside FULL_CORE·radius, feathered linearly to 0 at
 * `radius`.
 *
 * Hot-path contract: volumes are binned per segment and `forceNear`
 * tests only bins segIdx−1 .. segIdx+1, allocation-free — it runs per
 * racer per decision at pack sizes up to N=9999.
 */
import type { Placement, Vec3 } from "../shared/types.ts";

export interface FanVolume {
  /** Capsule start — the fan face, world coords. */
  origin: Vec3;
  /** Unit blow direction, world frame. */
  dir: Vec3;
  /** Capsule length, world units (axial falloff hits 0 here). */
  length: number;
  /** Capsule radius, world units (radial feather hits 0 here). */
  radius: number;
  /** Acceleration at the fan face, world u/s². */
  strength: number;
  /** Gust wall (M19a): blow only during the duty window each period. */
  pulse?: { period: number; duty: number; phase: number };
  /** Vortex (M19a): force tangential around the axis, not along it. */
  swirl?: boolean;
}

/** Radial fraction at full strength; feather over the outer band. */
export const FULL_CORE = 0.6;

/** Edge-ramp fraction of the on-window: force eases in/out over this
 * slice of the duty window instead of stepping (a step force reads as a
 * collision and double-integrates poorly at decision cadence). */
export const PULSE_RAMP = 0.2;

/** Pulse gain at sim time `t` — 0 outside the duty window, 1 in the
 * middle, cosine-eased over PULSE_RAMP·onTime at both edges. Pure in
 * (pulse, t): replays and headless runs agree by construction. */
export function pulseGain(
  pulse: { period: number; duty: number; phase: number } | undefined,
  t: number,
): number {
  if (!pulse) return 1;
  const period = pulse.period;
  if (!(period > 0)) return 1;
  const on = Math.min(1, Math.max(0, pulse.duty)) * period;
  if (on <= 0) return 0;
  const tau = (((t + pulse.phase) % period) + period) % period;
  if (tau >= on) return 0;
  const ramp = on * PULSE_RAMP;
  const edge = Math.min(tau, on - tau);
  if (edge >= ramp) return 1;
  return 0.5 - 0.5 * Math.cos(Math.PI * (edge / ramp));
}

// Family defaults (world units, tuned for TRACK_SCALE=2 endless pieces).
// Cross-wind (standing fan): 6.5 u/s² at the face over a 4-unit throw —
// the channel centerline sits ~2 units out, so mid-channel marbles feel
// ~3.3 u/s² laterally against a corridor spring that caps at ±10: lines
// bend visibly without pinning anyone to the wall.
export const CROSS_STRENGTH = 6.5;
export const CROSS_LENGTH = 4.0;
export const CROSS_RADIUS = 1.0;
// Updraft (table fan): 9 u/s² against GRAVITY_Y=−20 — a marble in the
// column runs at roughly half weight (visible lift wobble, not orbit)
// over a short 2.2-unit column.
export const UPDRAFT_STRENGTH = 9.0;
export const UPDRAFT_LENGTH = 2.2;
export const UPDRAFT_RADIUS = 0.9;

/** Hub height above the placement origin, in TRACK units (×scale at
 * derivation): standing fans are 1 unit tall with the disc centered
 * ~0.55 up; table-fan blades sit ~0.42 up in a 0.5-tall housing. */
export const STANDING_HUB = 0.55;
export const TABLE_HUB = 0.42;

/** Dir index → world step (0=+x, 1=+z, 2=−x, 3=−z). */
const DIR_VEC: readonly (readonly [number, number, number])[] = [
  [1, 0, 0],
  [0, 0, 1],
  [-1, 0, 0],
  [0, 0, -1],
];

/** Derive the wind capsule for one placement, or null when the piece is
 * not a fan. Standing fans blow along local +z (world dir (1−r)%4 —
 * the ledge fans' rotation ((1−facing)%4) makes that exactly `facing`);
 * table fans blow straight up regardless of yaw. */
export function fanVolumeFor(p: Placement): FanVolume | null {
  if (!p.pieceId.startsWith("fan-")) return null;
  const s = p.scale ?? 1;
  const standing = p.pieceId.startsWith("fan-standing");
  let dx: number, dy: number, dz: number;
  let hub: number;
  let strength: number, length: number, radius: number;
  if (standing) {
    const d = DIR_VEC[(1 - p.rotation + 4) % 4];
    // Render mirror is scale.x = −s applied after yaw: flip world x.
    dx = p.mirrored ? -d[0] : d[0];
    dy = d[1];
    dz = d[2];
    hub = STANDING_HUB * s;
    strength = CROSS_STRENGTH;
    length = CROSS_LENGTH;
    radius = CROSS_RADIUS;
  } else {
    dx = 0;
    dy = 1;
    dz = 0;
    hub = TABLE_HUB * s;
    strength = UPDRAFT_STRENGTH;
    length = UPDRAFT_LENGTH;
    radius = UPDRAFT_RADIUS;
  }
  const w = p.wind;
  const off = w?.offset;
  const v: FanVolume = {
    origin: [
      p.pos[0] + (off?.[0] ?? 0),
      p.pos[1] + hub + (off?.[1] ?? 0),
      p.pos[2] + (off?.[2] ?? 0),
    ],
    // A swirl column stands on its vertical axis regardless of the fan's
    // yaw — the housing points at the channel, the eddy spins beside it.
    dir: w?.swirl ? [0, 1, 0] : [dx, dy, dz],
    length: w?.length ?? length,
    radius: w?.radius ?? radius,
    strength: w?.strength ?? strength,
  };
  if (w?.pulse) v.pulse = w.pulse;
  if (w?.swirl) v.swirl = true;
  return v;
}

/** All wind capsules of a segment's placements (build-time, cold path). */
export function fanVolumesOf(placements: readonly Placement[]): FanVolume[] {
  const out: FanVolume[] = [];
  for (const p of placements) {
    const v = fanVolumeFor(p);
    if (v) out.push(v);
  }
  return out;
}

/** Per-segment wind-volume registry. */
export class FanField {
  private readonly bins = new Map<number, readonly FanVolume[]>();
  /** Accumulated SIM time (seconds) — gates pulsed volumes. The session
   * advances it from the same dt stream advanceTime drives. */
  private t = 0;

  setTime(t: number): void {
    this.t = t;
  }

  addSegment(index: number, volumes: readonly FanVolume[]): void {
    if (volumes.length > 0) this.bins.set(index, volumes);
    else this.bins.delete(index);
  }

  removeSegment(index: number): void {
    this.bins.delete(index);
  }

  clear(): void {
    this.bins.clear();
  }

  /** Accumulate the wind force at `pos` for a marble of `mass` into
   * `out` (+=, caller zeroes). Tests only segments segIdx−1..segIdx+1 —
   * O(nearby volumes), allocation-free. Returns true when any capsule
   * contributed. */
  forceNear(
    segIdx: number,
    pos: readonly [number, number, number],
    mass: number,
    out: [number, number, number],
  ): boolean {
    let hit = false;
    for (let k = segIdx - 1; k <= segIdx + 1; k++) {
      const vols = this.bins.get(k);
      if (!vols) continue;
      if (this.probeBin(vols, pos, mass, out)) hit = true;
    }
    return hit;
  }

  /** Cold-path variant scanning every bin (debug/tests). */
  forceAt(
    pos: readonly [number, number, number],
    mass: number,
    out: [number, number, number],
  ): boolean {
    let hit = false;
    for (const vols of this.bins.values()) {
      if (this.probeBin(vols, pos, mass, out)) hit = true;
    }
    return hit;
  }

  private probeBin(
    vols: readonly FanVolume[],
    pos: readonly [number, number, number],
    mass: number,
    out: [number, number, number],
  ): boolean {
    let hit = false;
    for (const v of vols) {
      const gain = v.pulse ? pulseGain(v.pulse, this.t) : 1;
      if (gain <= 0) continue;
      const rx = pos[0] - v.origin[0];
      const ry = pos[1] - v.origin[1];
      const rz = pos[2] - v.origin[2];
      const axial = rx * v.dir[0] + ry * v.dir[1] + rz * v.dir[2];
      if (axial < 0 || axial >= v.length) continue;
      const rad2 = rx * rx + ry * ry + rz * rz - axial * axial;
      if (rad2 >= v.radius * v.radius) continue;
      const rad = Math.sqrt(Math.max(0, rad2));
      const core = v.radius * FULL_CORE;
      const feather = rad <= core ? 1 : (v.radius - rad) / (v.radius - core);
      const f = mass * v.strength * (1 - axial / v.length) * feather * gain;
      if (v.swirl) {
        // Vortex: push TANGENTIALLY around the axis — dir × radial, so a
        // marble entering the column is deflected sideways, arcing around
        // the center instead of being blown along the axis. At the exact
        // center there is no tangent (rad ~ 0) and no force.
        if (rad < 1e-6) continue;
        const px = (rx - axial * v.dir[0]) / rad;
        const py = (ry - axial * v.dir[1]) / rad;
        const pz = (rz - axial * v.dir[2]) / rad;
        out[0] += f * (v.dir[1] * pz - v.dir[2] * py);
        out[1] += f * (v.dir[2] * px - v.dir[0] * pz);
        out[2] += f * (v.dir[0] * py - v.dir[1] * px);
      } else {
        out[0] += f * v.dir[0];
        out[1] += f * v.dir[1];
        out[2] += f * v.dir[2];
      }
      hit = true;
    }
    return hit;
  }
}
